import {
  BLUE_YONDER_PROVIDER,
  RETURN_BEAR_PROVIDER,
  VIRTUAL_PROVIDER,
} from "@redotech/redo-model/dropoff/dropoff-provider";
import { WebhookEventType } from "@redotech/redo-model/integrations/returns/blueyonder/blueyonder";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";

// ---------------------------------------------------------------------------
// Virtual (UniUni) tracking
// ---------------------------------------------------------------------------

const VirtualTrackingEventSchema = z.object({
  status: z.string(),
  statusCode: z.number(),
  timestamp: z.number(),
  location: z
    .object({
      name: z.string().optional(),
      latitude: z.number().optional(),
      longitude: z.number().optional(),
    })
    .optional(),
  description: z.string().optional(),
});

const VirtualDropoffTrackingSchema = z.object({
  provider: z.literal(VIRTUAL_PROVIDER),
  orderNumber: z.string(),
  trackingId: z.string(),
  status: z.string(),
  events: z.array(VirtualTrackingEventSchema),
  labelUrl: z.string().optional(),
});

// ---------------------------------------------------------------------------
// ReturnBear tracking (placeholder for future use)
// ---------------------------------------------------------------------------

const ReturnBearDropoffTrackingSchema = z.object({
  provider: z.literal(RETURN_BEAR_PROVIDER),
});

// ---------------------------------------------------------------------------
// BlueYonder tracking
// ---------------------------------------------------------------------------

const BlueYonderTrackingEventSchema = z.object({
  eventType: createEnum(WebhookEventType),
  eventDateTime: z.string(),
  eventId: z.string(),
});

const BlueYonderDropoffTrackingSchema = z.object({
  provider: z.literal(BLUE_YONDER_PROVIDER),
  events: z.array(BlueYonderTrackingEventSchema),
});

// ---------------------------------------------------------------------------
// Discriminated union output
// ---------------------------------------------------------------------------

const DropoffTrackingItemSchema = z.discriminatedUnion("provider", [
  VirtualDropoffTrackingSchema,
  ReturnBearDropoffTrackingSchema,
  BlueYonderDropoffTrackingSchema,
]);

export const getDropoffTracking = {
  name: "getDropoffTracking" as const,
  input: z.object({ returnId: zExt.objectId() }),
  output: z.object({ trackingResults: z.array(DropoffTrackingItemSchema) }),
};

export type DropoffTrackingItem = z.infer<typeof DropoffTrackingItemSchema>;
export type VirtualDropoffTracking = z.infer<
  typeof VirtualDropoffTrackingSchema
>;
export type VirtualTrackingEvent = z.infer<typeof VirtualTrackingEventSchema>;
export type BlueYonderDropoffTracking = z.infer<
  typeof BlueYonderDropoffTrackingSchema
>;
